import { FilterConditionOperators, AccessLevel, SymbolSetName } from '../../../common/enums';
import { Column } from '../../../common/interfaces';
import { ReportColumnOptionsResponse } from '../../../common/interfaces/report/types';
import { MultiSelectItem } from '../../../components/MultiSelect/MultiSelect.interface';
import { SelectOption } from '../../Form/Interfaces/SelectOption.interface';
import { DualInput } from '../../../common/interfaces';
import { LanguageElements } from '../../../language-elements/LanguageElements';
import { Filter } from '../../../common/classes/Filter/Filter';
import { MAX_FILTER_NAME } from '../../../common/constants';
import { FilterActionType, FilterUI } from './types';
import { SymbolImageUrlResolver } from '../../../components/Picklist/SymbolPickerImages';

/**
 * Maps FilterConditionOperators enum values to their corresponding language elements
 *
 * @param filterConditionOperators The operator to get the label for
 * @param languageElements The language elements object containing translations
 * @returns The localized label for the operator
 */
export const getFilterConditionOperatorsLabel = (filterConditionOperators: FilterConditionOperators, languageElements: LanguageElements): string => {
    switch (filterConditionOperators) {
        case FilterConditionOperators.IS_ONE_OF:
            return languageElements.lbl_is_one_of;
        case FilterConditionOperators.IS_NOT_ONE_OF:
            return languageElements.lbl_is_not_one_of;
        case FilterConditionOperators.IS_CHECKED:
            return languageElements.lbl_is_checked;
        case FilterConditionOperators.IS_NOT_CHECKED:
            return languageElements.lbl_is_not_checked;
        case FilterConditionOperators.IS_EQUAL_TO:
            return languageElements.lbl_is_equal_to;
        case FilterConditionOperators.IS_NOT_EQUAL_TO:
            return languageElements.lbl_is_not_equal_to;
        case FilterConditionOperators.CONTAINS:
            return languageElements.lbl_contains;
        case FilterConditionOperators.DOES_NOT_CONTAIN:
            return languageElements.lbl_does_not_contain;
        case FilterConditionOperators.IS_BLANK:
            return languageElements.lbl_is_blank;
        case FilterConditionOperators.IS_NOT_BLANK:
            return languageElements.lbl_is_not_blank;
        case FilterConditionOperators.HAS_ANY_OF:
            return languageElements.lbl_has_any_of;
        case FilterConditionOperators.HAS_NONE_OF:
            return languageElements.lbl_has_none_of;
        case FilterConditionOperators.HAS_ALL_OF:
            return languageElements.lbl_has_all_of;
        case FilterConditionOperators.DOES_NOT_HAVE_ALL_OF:
            return languageElements.lbl_does_not_have_all_of;
        case FilterConditionOperators.IS_GREATER_THAN:
            return languageElements.lbl_is_greater_than;
        case FilterConditionOperators.IS_LESS_THAN:
            return languageElements.lbl_is_less_than;
        case FilterConditionOperators.IS_GREATER_THAN_OR_EQUAL_TO:
            return languageElements.lbl_is_greater_than_or_equal_to;
        case FilterConditionOperators.IS_LESS_THAN_OR_EQUAL_TO:
            return languageElements.lbl_is_less_than_or_equal_to;
        case FilterConditionOperators.IS_BETWEEN:
            return languageElements.lbl_is_between;
        case FilterConditionOperators.IS_NOT_BETWEEN:
            return languageElements.lbl_is_not_between;
        case FilterConditionOperators.IS_A_NUMBER:
            return languageElements.lbl_is_a_number;
        case FilterConditionOperators.IS_NOT_A_NUMBER:
            return languageElements.lbl_is_not_a_number;
        case FilterConditionOperators.IS_A_DATE:
            return languageElements.lbl_is_a_date;
        case FilterConditionOperators.IS_NOT_A_DATE:
            return languageElements.lbl_is_not_a_date;
        default:
            return '';
    }
};

/**
 * Convert condition operators to select options
 *
 * @param operators Array of operator enum values
 * @param languageElements The language elements object containing translations
 * @returns Array of SelectOption objects
 */
export const getConditionOperatorOptions = (
    operators: string[] = [],
    languageElements: LanguageElements = {} as LanguageElements
): SelectOption[] => {
    if (!operators) {
        return [];
    }

    return operators.map(
        (operator: FilterConditionOperators) =>
            ({
                value: operator,
                label: getFilterConditionOperatorsLabel(operator, languageElements),
            } as SelectOption)
    );
};

/**
 * Convert condition fields to select options
 *
 * @param conditionFields Array of column objects
 * @returns Array of SelectOption objects
 */
export const getConditionFieldOptions = (conditionFields: Column[] = []): SelectOption[] => {
    if (!conditionFields) {
        return [];
    }

    return conditionFields.map(
        (field: Column) =>
            ({
                value: field.id || field.virtualId,
                label: field.title,
            } as SelectOption)
    );
};

/**
 * Format multi-select items
 *
 * @param filterInput Array of string values
 * @param conditionMultiSelectOptions Available multi-select options
 * @returns Formatted multi-select items
 */
export const formatMultiSelectItems = (filterInput: string[] = [], conditionMultiSelectOptions: MultiSelectItem[] = []): MultiSelectItem[] => {
    if (!filterInput) {
        return [];
    }

    return filterInput.map((item: string) => {
        const multiSelectOption = conditionMultiSelectOptions.find((option) => option.value === item);
        return multiSelectOption
            ? multiSelectOption
            : {
                  value: item,
                  label: item,
                  subLabel: '',
                  isValid: true,
                  controlIdPrefix: 'ctl',
              };
    });
};

// Lookup table for filter selection based on operator type
const filterSelectionHandlers = {
    noValue: [FilterConditionOperators.IS_CHECKED, FilterConditionOperators.IS_NOT_CHECKED],
    multiSelect: [
        FilterConditionOperators.IS_ONE_OF,
        FilterConditionOperators.IS_NOT_ONE_OF,
        FilterConditionOperators.HAS_ANY_OF,
        FilterConditionOperators.HAS_NONE_OF,
        FilterConditionOperators.HAS_ALL_OF,
        FilterConditionOperators.DOES_NOT_HAVE_ALL_OF,
    ],
    singleValue: [
        FilterConditionOperators.CONTAINS,
        FilterConditionOperators.DOES_NOT_CONTAIN,
        FilterConditionOperators.IS_EQUAL_TO,
        FilterConditionOperators.IS_NOT_EQUAL_TO,
        FilterConditionOperators.IS_GREATER_THAN,
        FilterConditionOperators.IS_LESS_THAN,
        FilterConditionOperators.IS_GREATER_THAN_OR_EQUAL_TO,
        FilterConditionOperators.IS_LESS_THAN_OR_EQUAL_TO,
    ],
    dualValue: [FilterConditionOperators.IS_BETWEEN, FilterConditionOperators.IS_NOT_BETWEEN],
};

/**
 * Get the appropriate filter selection based on operator type
 *
 * @param filterInput The filter input value
 * @param conditionMultiSelectOptions Available multi-select options
 * @param operator The filter condition operator
 * @returns The formatted filter selection
 */
export const getFilterSelection = (
    filterInput: string[] | string | DualInput = [],
    conditionMultiSelectOptions: MultiSelectItem[] = [],
    operator?: FilterConditionOperators
): MultiSelectItem[] | string | undefined | DualInput => {
    if (!operator) {
        return '';
    }

    if (filterSelectionHandlers.noValue.includes(operator)) {
        return undefined;
    }

    if (filterSelectionHandlers.multiSelect.includes(operator)) {
        return formatMultiSelectItems(filterInput as string[], conditionMultiSelectOptions);
    }

    if (filterSelectionHandlers.singleValue.includes(operator)) {
        return typeof filterInput === 'string' ? filterInput : '';
    }

    if (filterSelectionHandlers.dualValue.includes(operator)) {
        return filterInput as DualInput;
    }

    return '';
};

/**
 * Look up filter items from the map
 *
 * @param filterItemsMap Map of filter items by column ID
 * @param columnId The column ID to look up
 * @returns Array of MultiSelectItems for the column
 */
export const lookupFilterItems = (filterItemsMap: Map<number, Map<string, MultiSelectItem>> = new Map(), columnId: number): MultiSelectItem[] => {
    let filterItems: MultiSelectItem[] = [];

    if (filterItemsMap && columnId && filterItemsMap.has(columnId)) {
        filterItems = Array.from(filterItemsMap.get(columnId)!.values());
    }

    return filterItems;
};

/**
 * Get the appropriate filter for editing based on the filter action type
 *
 * @param filterAction The type of filter action being performed
 * @param filtersUI Array of existing filters
 * @param editFilterIndex Index of the filter being edited
 * @param fields Array of available columns
 * @param languageElements Language elements for localization
 * @param currentUserAccessLevel Current user's access level
 * @returns FilterUI object ready for editing
 */
export const getFilterForEditing = (
    filterAction: FilterActionType,
    filtersUI: FilterUI[] | undefined,
    editFilterIndex: number,
    fields: Column[],
    languageElements: LanguageElements,
    currentUserAccessLevel: AccessLevel
): FilterUI => {
    if (filterAction === FilterActionType.NEW_FILTER) {
        return new Filter(fields, undefined, false).toDisplayModel();
    }

    if (filterAction === FilterActionType.DUPLICATE_FILTER && filtersUI && editFilterIndex !== -1) {
        const filterToCopy = new Filter(fields, filtersUI[editFilterIndex], false);
        filterToCopy.setName(languageElements.ADHOC_FILTER_COPY_OF(filterToCopy.name).slice(0, MAX_FILTER_NAME));
        filterToCopy.setSharing(currentUserAccessLevel === AccessLevel.USER ? false : filterToCopy.shared);
        return new Filter(fields, filterToCopy, false).toDisplayModel();
    }

    return filtersUI && editFilterIndex !== -1 ? filtersUI[editFilterIndex] : ({} as FilterUI);
};

/**
 * Convert imageURL identifier to actual symbol SVG path
 * Similar to how FormFieldSymbolPicklist works with SymbolImageUrlResolver
 *
 * @param imageURL The image URL identifier (e.g., "signal5000", "signal5025")
 * @returns The actual SVG path or undefined if conversion fails
 */
const convertImageUrlToSymbol = (imageURL: string): string | undefined => {
    if (!imageURL) {
        return undefined;
    }

    // Parse the imageURL identifier to extract symbol set and index
    // Examples: "signal5000" -> SymbolSetName.SIGNAL, index 0
    //          "signal5025" -> SymbolSetName.SIGNAL, index 1
    //          "signal5050" -> SymbolSetName.SIGNAL, index 2
    if (imageURL.startsWith('signal')) {
        const numericPart = imageURL.replace('signal', '');
        const numericValue = parseInt(numericPart, 10);

        if (!isNaN(numericValue)) {
            // Convert numeric value to index (5000 -> 0, 5025 -> 1, 5050 -> 2, etc.)
            const index = Math.floor((numericValue - 5000) / 25);
            return SymbolImageUrlResolver(SymbolSetName.SIGNAL, index);
        }
    }

    // Add more symbol set conversions here as needed
    // For now, return undefined for unrecognized patterns
    return undefined;
};

/**
 * Get condition multi-select options from report column options
 *
 * @param reportColumnOptions The report column options response
 * @param columnId The column ID to get options for
 * @returns Array of MultiSelectItem objects for the column
 */
export const getConditionMultiSelectOptions = (
    reportColumnOptions: ReportColumnOptionsResponse | null | undefined,
    columnId: string | number | undefined
): MultiSelectItem[] => {
    const conditionMultiSelectOptions: MultiSelectItem[] = [];

    if (reportColumnOptions && columnId) {
        const columnOptions = reportColumnOptions.reportColumnOptions.find((option) => (option as any).reportColumnId === Number(columnId));

        if (Array.isArray(columnOptions?.columnOptions)) {
            columnOptions?.columnOptions?.forEach((option: any, index: number) => {
                conditionMultiSelectOptions.push({
                    id: index,
                    value: option.displayValue,
                    label: option.displayValue,
                    isValid: true,
                    controlIdPrefix: `filter-option-${index}`,
                    symbol: convertImageUrlToSymbol(option.imageURL),
                });
            });
        }
    }

    return conditionMultiSelectOptions;
};
